import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { Box, Card, CardContent, CircularProgress, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { getReservationStats } from '../../api/endpoints/reservationStats';
import type { ProviderStat, RouteStat, TripTypeStat, YearlyStat } from '../../api/types/reservationStats';
import { ApiErrorAlert } from '../../components/ApiErrorAlert';

/** Chart colors — validated together via the dataviz skill's `validate_palette.js`
 *  (categorical, light mode, 3 slots: all checks pass). `CANCELLED` is a deliberately
 *  darker red than `theme.palette.error.main` — the theme's lighter error red sits too
 *  close to the secondary orange on this endpoint's 3-series charts (adjacent-pair CVD
 *  ΔE 11.4, below the 15 normal-vision floor) once both appear together in a stacked/
 *  grouped bar, so this is a separate "chart-only" token, not a theme change. */
const BOOKED_COLOR = '#2954E8';
const PREBOOKED_COLOR = '#FF7A45';
const CANCELLED_COLOR = '#B32D2E';

export interface StatsRange {
  startDate: string;
  endDate: string;
}

/** Last 365 days ending today — `GetReservationStats` rejects ranges over 365 days
 *  (confirmed live, see api/types/reservationStats.ts). */
export function defaultStatsRange(): StatsRange {
  return {
    startDate: dayjs().subtract(364, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  };
}

function useReservationStatsQuery(range?: StatsRange) {
  const effectiveRange = range ?? defaultStatsRange();
  return useQuery({
    queryKey: ['reservationStats', effectiveRange.startDate, effectiveRange.endDate],
    queryFn: () => getReservationStats(effectiveRange),
  });
}

function StatTile({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" component="div">
          {label}
        </Typography>
        <Typography variant="h4" sx={{ color: color ?? 'text.primary', lineHeight: 1.3 }}>
          {value.toLocaleString('tr-TR')}
        </Typography>
      </CardContent>
    </Card>
  );
}

/** Thin horizontal bar, rounded ends, direct label — used by both the provider
 *  ranking and the top-routes list (sequential job: one hue, magnitude by length,
 *  identity carried by the direct label, not by color). */
function RankedBar({ label, value, maxValue, color, trailing }: {
  label: string;
  value: number;
  maxValue: number;
  color: string;
  trailing?: string;
}) {
  const widthPct = maxValue > 0 ? Math.max(4, (value / maxValue) * 100) : 0;
  return (
    <Box sx={{ mb: 1.25 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {trailing ?? value.toLocaleString('tr-TR')}
        </Typography>
      </Stack>
      <Box sx={{ height: 10, borderRadius: 5, backgroundColor: 'action.hover', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${widthPct}%`, borderRadius: 5, backgroundColor: color }} />
      </Box>
    </Box>
  );
}

function ProviderBreakdownCard({ providerStats }: { providerStats: ProviderStat[] }) {
  const rows = useMemo(
    () => [...providerStats].sort((a, b) => (b.total ?? 0) - (a.total ?? 0)).slice(0, 8),
    [providerStats]
  );
  const maxValue = Math.max(1, ...rows.map((r) => r.total ?? 0));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Sağlayıcıya Göre Dağılım
        </Typography>
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Veri bulunamadı.
          </Typography>
        )}
        {rows.map((row) => (
          <RankedBar
            key={row.provider}
            label={row.provider ?? '—'}
            value={row.total ?? 0}
            maxValue={maxValue}
            color={BOOKED_COLOR}
            trailing={`${(row.total ?? 0).toLocaleString('tr-TR')}${row.share != null ? ` · %${row.share}` : ''}`}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function TopRoutesCard({ topRoutes }: { topRoutes: RouteStat[] }) {
  const rows = useMemo(() => [...topRoutes].sort((a, b) => (b.count ?? 0) - (a.count ?? 0)).slice(0, 6), [topRoutes]);
  const maxValue = Math.max(1, ...rows.map((r) => r.count ?? 0));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          En Çok Kullanılan Güzergahlar
        </Typography>
        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Veri bulunamadı.
          </Typography>
        )}
        {rows.map((row, i) => (
          <RankedBar
            key={`${row.origin}-${row.destination}-${i}`}
            label={`${row.originCity ?? row.origin ?? '—'} → ${row.destinationCity ?? row.destination ?? '—'}`}
            value={row.count ?? 0}
            maxValue={maxValue}
            color={PREBOOKED_COLOR}
          />
        ))}
      </CardContent>
    </Card>
  );
}

const LEGEND = [
  { label: 'Bilet Sayısı', color: BOOKED_COLOR },
  { label: 'Ön Rezervasyon', color: PREBOOKED_COLOR },
  { label: 'İptal', color: CANCELLED_COLOR },
];

function Legend() {
  return (
    <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
      {LEGEND.map((l) => (
        <Stack key={l.label} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: l.color }} />
          <Typography variant="caption" color="text.secondary">
            {l.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/** Grouped bar per year — 3 series (Booked/Prebooked/Cancelled), categorical color,
 *  legend once above (not repeated per group) since bars are too narrow for direct
 *  labels — matches the skill's "≥2 series → legend always present" rule. Bar height
 *  is proportional to the largest single value across the whole chart (one shared
 *  scale, never per-year), so years stay comparable to each other. */
function YearlyTrendCard({ yearlyStats }: { yearlyStats: YearlyStat[] }) {
  const rows = useMemo(() => [...yearlyStats].sort((a, b) => (a.year ?? 0) - (b.year ?? 0)), [yearlyStats]);
  const maxValue = Math.max(1, ...rows.flatMap((r) => [r.booked ?? 0, r.prebooked ?? 0, r.cancelled ?? 0]));
  const CHART_HEIGHT = 120;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Yıllara Göre Trend
        </Typography>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Veri bulunamadı.
          </Typography>
        ) : (
          <>
            <Legend />
            <Stack direction="row" spacing={3} sx={{ alignItems: 'flex-end', height: CHART_HEIGHT, px: 1 }}>
              {rows.map((row) => (
                <Stack key={row.year} spacing={0.5} sx={{ alignItems: 'center', flex: 1 }}>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-end', height: CHART_HEIGHT - 24 }}>
                    {[
                      { value: row.booked ?? 0, color: BOOKED_COLOR, label: 'Bilet Sayısı' },
                      { value: row.prebooked ?? 0, color: PREBOOKED_COLOR, label: 'Ön Rezervasyon' },
                      { value: row.cancelled ?? 0, color: CANCELLED_COLOR, label: 'İptal' },
                    ].map((bar) => (
                      <Tooltip key={bar.label} title={`${bar.label}: ${bar.value.toLocaleString('tr-TR')}`}>
                        <Box
                          sx={{
                            width: 14,
                            height: `${Math.max(2, (bar.value / maxValue) * 100)}%`,
                            borderRadius: '4px 4px 0 0',
                            backgroundColor: bar.color,
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {row.year}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Single 100%-width stacked bar, 3 segments — part-to-whole job, categorical color,
 *  direct-labeled underneath (3 series stays in the "color alone is comfortable"
 *  range per the skill's series-count ladder). `tripType` values are already Turkish
 *  text from the backend (see api/types/reservationStats.ts). */
function TripTypeCard({ tripTypeStats }: { tripTypeStats: TripTypeStat[] }) {
  const total = tripTypeStats.reduce((sum, s) => sum + (s.count ?? 0), 0);
  const palette = [BOOKED_COLOR, PREBOOKED_COLOR, CANCELLED_COLOR, '#8A93A6'];

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Sefer Tipi Dağılımı
        </Typography>
        {tripTypeStats.length === 0 || total === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Veri bulunamadı.
          </Typography>
        ) : (
          <>
            <Stack direction="row" sx={{ height: 14, borderRadius: 7, overflow: 'hidden' }}>
              {tripTypeStats.map((s, i) => (
                <Tooltip key={s.tripType} title={`${s.tripType}: ${s.count}`}>
                  <Box
                    sx={{
                      width: `${((s.count ?? 0) / total) * 100}%`,
                      backgroundColor: palette[i % palette.length],
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
              {tripTypeStats.map((s, i) => (
                <Stack key={s.tripType} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                  <Box
                    sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: palette[i % palette.length] }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {s.tripType} ({s.count})
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * "ReservationStats/GetReservationStats" — a handful of Anasayfa dashboard widgets
 * over this endpoint (see api/endpoints/reservationStats.ts / api/types/
 * reservationStats.ts for the full live-confirmed shape). No charting library is
 * used — @mui/x-charts was never added to this project and @mui/x-data-grid is
 * banned outright under this project's pinned TypeScript 4.9.5 (see other modules'
 * comments) — every chart here is hand-rolled MUI `Box`/`Stack` bars, following the
 * dataviz skill's form/color rules: sequential single-hue bars for magnitude
 * (provider ranking, top routes), categorical color for the two 3-series charts
 * (yearly trend, trip type), direct labels everywhere so color is never the only
 * carrier of meaning. Always queries the last 365 days (the endpoint's own max range).
 */
export function ReservationStatsSection({
  range,
  title = 'Uçak İstatistikleri (Son 365 Gün)',
}: {
  range?: StatsRange;
  title?: string;
}) {
  const query = useReservationStatsQuery(range);
  const data = query.data;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isPending && <CircularProgress size={24} />}

      {data && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatTile label="Toplam Rezervasyon" value={data.totalCount ?? 0} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatTile label="Bilet Sayısı" value={data.bookedCount ?? 0} color={BOOKED_COLOR} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatTile label="Ön Rezervasyon" value={data.prebookedCount ?? 0} color={PREBOOKED_COLOR} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatTile label="İptal" value={data.cancelledCount ?? 0} color={CANCELLED_COLOR} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ProviderBreakdownCard providerStats={data.providerStats ?? []} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <YearlyTrendCard yearlyStats={data.yearlyStats ?? []} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TopRoutesCard topRoutes={data.topRoutes ?? []} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TripTypeCard tripTypeStats={data.tripTypeStats ?? []} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
